export const MAPS_READY_GLOBAL_CALLBACK = 'printos.gazelle.$gzGoogleMaps.mapsReady';

export const name = '$gzGoogleMaps';
export default function $gzGoogleMaps(
  rnRegistry,
  rnHttp,
  rnLocale,
  $q,
  $state,
  $window
) {
  'ngInject';

  let maps = {};
  let mapsDeferred = {};
  let readyDeferred;
  let isMaps = false;

  const service = {
    mapsSupported,
    onReady,
    onMap,
    getMap,
    mapAdded,
    mapRemoved,
    getPlaceFromAddress,
    getPlaceFromLocation,
    getAddressFromPlace,
    getTimeZoneFromAddress,
    getFormattedAddress,
    getMapsKey,
  };
  return service;

  function onReady() {
    if (!readyDeferred) {
      return install();
    }
    return readyDeferred.promise;
  }

  function getMapsKey() {
    return rnRegistry.getKey('mapsKey');
  }

  function install() {
    return getMapsKey().then(apiKey => {
      isMaps = true;
      return addGoogleMapsScript(apiKey);
    });
  }

  function mapsSupported() {
    return isMaps;
  }

  function addGoogleMapsScript(apiKey) {
    if (readyDeferred) {
      return readyDeferred.promise;
    }

    readyDeferred = $q.defer();
    const mapsReady = () => {
      readyDeferred.resolve();
    };
    _.set($window, MAPS_READY_GLOBAL_CALLBACK, mapsReady);

    let script = $window.document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${MAPS_READY_GLOBAL_CALLBACK}`;
    let firstScript = $window.document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    return readyDeferred.promise;
  }

  function onMap(pairingId) {
    if (!pairingId) {
      pairingId = $state.current.name;
    }
    let map = getMap(pairingId);
    if (map) {
      return $q.resolve(map);
    }

    let deferred = mapsDeferred[pairingId];
    if (!deferred) {
      deferred = $q.defer();
      mapsDeferred[pairingId] = deferred;
    }
    return deferred.promise;
  }

  function getMap(pairingId) {
    if (!pairingId) {
      pairingId = $state.current.name;
    }
    return maps[pairingId];
  }

  function mapAdded(pairingId, map) {
    if (!pairingId) {
      pairingId = $state.current.name;
    }
    maps[pairingId] = map;

    let deferred = mapsDeferred[pairingId];
    if (deferred) {
      deferred.resolve(map);
      mapsDeferred[pairingId] = undefined;
    }
  }

  function mapRemoved(pairingId) {
    if (!pairingId) {
      pairingId = $state.current.name;
    }
    maps[pairingId] = undefined;
  }

  function getPlaceFromAddress(address) {
    let location = '';
    if (address.addressLine1) {
      location += address.addressLine1 + ' ';
    }
    if (address.addressLine2) {
      location += address.addressLine2 + ' ';
    }
    if (address.locality) {
      location += address.locality + ' ';
    }
    if (address.region) {
      location += address.region + ' ';
    }
    if (address.postalCode) {
      location += address.postalCode + ' ';
    }
    if (address.country) {
      location += address.country;
    }

    return getPlaceFromLocation(location);
  }

  function getPlaceFromLocation(location) {
    return getPlacePredictionFromLocation(location).then(place => {
      if (place) {
        return place;
      }
      // Fallback to geocoding API. Sadly some results from places API are asymmetric for some reason.
      return getGeocodeFromLocation(location);
    });
  }

  function getPlacePredictionFromLocation(location) {
    return onReady().then(() => {
      let deferred = $q.defer();

      let autoCompleteService = new $window.google.maps.places.AutocompleteService();
      autoCompleteService.getPlacePredictions({
        input: location,
        types: ['address'],
      }, (predictions, status) => {
        if (status !== $window.google.maps.places.PlacesServiceStatus.OK || predictions.length === 0) {
          return deferred.resolve();
        }

        let placesService = new $window.google.maps.places.PlacesService(document.createElement('div'));
        placesService.getDetails({placeId: predictions[0].place_id}, (place, placesStatus) => {
          if (placesStatus !== $window.google.maps.places.PlacesServiceStatus.OK) {
            return deferred.resolve();
          }

          return deferred.resolve(place);
        });
      });

      return deferred.promise;
    });
  }

  function getGeocodeFromLocation(location) {
    return $q.all([
      getMapsKey(),
      getGoogleLanguageCode(),
    ]).then(([mapsKey, language]) => {
      let config = {
        headers: {
          'If-Modified-Since': undefined,
          'Cache-Control': undefined
        },
        method: 'GET',
        url: 'https://maps.googleapis.com/maps/api/geocode/json',
        withCredentials: false,
        params: {
          key: mapsKey,
          address: location,
          language,
        }
      };

      return rnHttp.simpleRequest(config).then(resp => {
        if (resp.data.status === 'OK' && resp.data.results[0]) {
          return resp.data.results[0];
        }

        // If we don't get any results, return undefined
        $q.resolve(undefined);
      }).catch(error => {
        // If something blows up, return undefined
        $q.resolve();
      });
    });
  }

  function getGoogleLanguageCode() {
    return rnLocale.getBestFit().then(locale => {
      if (locale.shortName === 'zh') {
        return 'zh-CN';
      }
      return locale.shortName;
    });
  }

  function getAddressFromPlace(place) {
    let components = _.get(place, 'address_components');
    let address = {};
    if (!components) {
      return address;
    }

    // Address Line 1
    let address1 = '';
    let street_number = getAddressPart(components, 'street_number');
    if (street_number) {
      address1 += street_number.long_name;
    }

    let route = getAddressPart(components, 'route');
    if (route) {
      if (address1 !== '') {
        address1 += ' ';
      }
      address1 += route.long_name;
    }
    if (address1 !== '') {
      address.addressLine1 = address1;
    }

    // Address Line 2
    let address2 = '';
    let floor = getAddressPart(components, 'floor');
    if (floor) {
      address2 += floor.long_name;
    }

    let room = getAddressPart(components, 'room');
    if (room) {
      if (address2 !== '') {
        address2 += ' ';
      }
      address2 += room.long_name;
    }

    let box = getAddressPart(components, 'post_box');
    if (box) {
      if (address2 !== '') {
        address2 += ' ';
      }
      address2 += box.long_name;
    }

    let subpremise = getAddressPart(components, 'subpremise');
    if (subpremise) {
      if (address2 !== '') {
        address2 += ' ';
      }
      address2 += subpremise.long_name;
    }

    if (address2 !== '') {
      address.addressLine2 = address2;
    }

    // City/Locality
    let locality = getAddressPart(components, 'locality');
    if (locality) {
      address.locality = locality.long_name;
    } else {
      locality = getAddressPart(components, 'administrative_area_level_2');
      if (locality) {
        address.locality = locality.long_name;
      }
    }

    // State/Region
    let region = getAddressPart(components, 'administrative_area_level_1');
    if (region) {
      address.region = region.short_name;
    }

    // Postal Code
    let postalCode = '';
    let code = getAddressPart(components, 'postal_code');
    if (code) {
      postalCode += code.long_name;
    }

    let codeSuffix = getAddressPart(components, 'postal_code_suffix');
    if (codeSuffix) {
      if (postalCode !== '') {
        postalCode += '-';
      }
      postalCode += codeSuffix.long_name;
    }

    if (postalCode !== '') {
     address.postalCode = postalCode;
    }

    // Country
    let country = getAddressPart(components, 'country');
    if (country) {
      address.country = country.short_name;
    }

    return address;
  }

  function getTimeZoneFromAddress(address) {
    return getMapsKey().then(mapsKey => {
      return getPlaceFromAddress(address).then(place => {
        let config = {
          headers: {
            'If-Modified-Since': undefined,
            'Cache-Control': undefined
          },
          method: 'GET',
          url: 'https://maps.googleapis.com/maps/api/timezone/json',
          withCredentials: false,
          params: {
            key: mapsKey,
            timestamp: new Date().getTime() / 1000,
          }
        };

        let location = _.get(place, 'geometry.location');
        if (location) {
          if (_.isFunction(location.lat)) {
            _.set(config, 'params.location', location.lat() + ',' + location.lng());
          }
          else {
            _.set(config, 'params.location', location.lat + ',' + location.lng);
          }

          return rnHttp.simpleRequest(config).then(resp => {
            return resp.data;
          })
            .catch(error => {
              return $q.resolve();
            });
        }
        else {
          return $q.resolve();
        }
      });
    });
  }

  function getFormattedAddress(address) {
    let location = '';

    if (address) {
      if (address.addressLine1) {
        location += address.addressLine1 + ' ';
      }
      if (address.addressLine2) {
        location += address.addressLine2 + ' ';
      }
      if (address.locality) {
        location += address.locality + ' ';
      }
      if (address.region) {
        location += address.region + ' ';
      }
      if (address.postalCode) {
        location += address.postalCode + ' ';
      }
      if (address.country) {
        location += address.country;
      }
    }

    return location;
  }

  //////////////////////////
  /// Helpers
  //////////////////////////
  function getAddressPart(addressComponents, part) {
    return _.find(addressComponents, (o) => {
      return (o.types.indexOf(part) !== -1);
    });
  }
}



// WEBPACK FOOTER //
// ../~/@printos/gazelle/src/components/google-maps/google-maps.service.js